import { useState } from "react";
import { ArrowDown, ArrowUp, Check, ChevronLeft, ChevronRight, ListOrdered, Navigation, Phone, Route, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import RouteMap, { stopAddress } from "@/components/routes/RouteMap";

const google = (a) => `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(a)}&travelmode=driving`;
const apple = (a) => `https://maps.apple.com/?daddr=${encodeURIComponent(a)}&dirflg=d`;
const MAX = 10;
const googleAll = (list) => {
  const [dest, ...rest] = [...list].reverse();
  const wp = rest.reverse().map(encodeURIComponent).join("|");
  return `${google(dest)}${wp ? `&waypoints=${wp}` : ""}`;
};
const appleAll = (list) => `https://maps.apple.com/?dirflg=d&${list.map((a) => `daddr=${encodeURIComponent(a)}`).join("&")}`;

function ReorderBar({ picked, total, onStart, onApply, onCancel }) {
  if (!picked) return <Button variant="outline" className="w-full" onClick={onStart} data-testid="route-map-reorder"><ListOrdered size={14} /> Reorder by clicking pins</Button>;
  return (
    <div className="border border-emerald-500/40 bg-emerald-500/10 rounded-md p-3 space-y-2" data-testid="route-map-reorder-bar">
      <p className="text-sm">Click the pins (or names below) in the order you want to drive. <span className="font-mono" data-testid="route-map-picked-count">{picked.length} of {total}</span> picked; the rest keep their order at the end. Click a green pin again to undo it.</p>
      <div className="flex gap-2">
        <Button size="sm" onClick={onApply} disabled={!picked.length} data-testid="route-map-reorder-apply"><Check size={14} /> Apply order</Button>
        <Button size="sm" variant="ghost" onClick={onCancel} data-testid="route-map-reorder-cancel"><X size={14} /> Cancel</Button>
      </div>
    </div>
  );
}

export default function RouteMapPanel({ stops, onStartHere, onReorder }) {
  const [current, setCurrent] = useState(0);
  const [picked, setPicked] = useState(null);
  const i = Math.min(current, stops.length - 1);
  const s = stops[i];
  if (!s) return null;
  const addr = stopAddress(s);
  const rest = stops.slice(i, i + MAX).map(stopAddress).filter(Boolean);

  const pick = (k) => (picked ? setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k])) : setCurrent(k));
  const apply = () => {
    onReorder([...picked.map((k) => stops[k]), ...stops.filter((_, k) => !picked.includes(k))]);
    setPicked(null); setCurrent(0);
  };
  const move = (k, to) => {
    const next = [...stops];
    const [x] = next.splice(k, 1);
    next.splice(to, 0, x);
    onReorder(next);
  };

  return (
    <div className="border rounded-lg bg-card p-4 grid lg:grid-cols-[1fr_320px] gap-4" data-testid="route-map-panel">
      <RouteMap stops={stops} current={i} onPick={pick} picked={picked} />
      <div className="space-y-4">
        {onReorder && <ReorderBar picked={picked} total={stops.length} onStart={() => setPicked([])} onApply={apply} onCancel={() => setPicked(null)} />}
        <div className="border rounded-md p-4 bg-secondary/40" data-testid="route-next-stop">
          <p className="text-xs font-mono uppercase tracking-widest text-amber-400">Next stop {i + 1} of {stops.length}</p>
          <p className="font-semibold mt-2" data-testid="route-next-name">{s.name || "—"}</p>
          <p className="text-sm text-muted-foreground">{addr}</p>
          {s.job && <p className="text-xs font-mono text-muted-foreground mt-1">Job # {s.job}</p>}
          {s.phone && <a href={`tel:${s.phone}`} className="text-sm inline-flex items-center gap-1 mt-1 text-primary hover:underline" data-testid="route-next-phone"><Phone size={12} /> {s.phone}</a>}
          <p className="text-xs text-muted-foreground mt-4" data-testid="route-all-stops-note">
            All stops from here{rest.length < stops.length - i ? ` (next ${rest.length} — Google allows ${MAX} at a time)` : ` (${rest.length})`}
          </p>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <Button asChild><a href={googleAll(rest)} target="_blank" rel="noreferrer" data-testid="route-directions-google"><Navigation size={14} /> Google Maps</a></Button>
            <Button asChild variant="outline"><a href={appleAll(rest)} target="_blank" rel="noreferrer" data-testid="route-directions-apple"><Navigation size={14} /> Apple Maps</a></Button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Just this stop:{" "}
            <a href={google(addr)} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="route-directions-google-one">Google</a>{" · "}
            <a href={apple(addr)} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="route-directions-apple-one">Apple</a>
          </p>
          <div className="flex justify-between mt-3">
            <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => setCurrent(i - 1)} data-testid="route-prev-stop"><ChevronLeft size={14} /> Back</Button>
            <Button size="sm" variant="ghost" disabled={i === stops.length - 1} onClick={() => setCurrent(i + 1)} data-testid="route-done-next">Done, next <ChevronRight size={14} /></Button>
          </div>
          {onStartHere && stops.length > 2 && (
            <Button size="sm" variant="outline" className="w-full mt-2" onClick={async () => { await onStartHere(i); setCurrent(0); }} data-testid="route-start-here">
              <Route size={14} /> Start here, then closest next
            </Button>
          )}
        </div>
        <ol className="max-h-[220px] overflow-y-auto divide-y border rounded-md text-sm" data-testid="route-map-list">
          {stops.map((x, k) => (
            <li key={x.record_id} className="flex items-center">
              <button type="button" onClick={() => pick(k)} data-testid={`route-map-stop-${k}`}
                className={`flex-1 min-w-0 text-left px-3 py-2 transition-colors ${picked?.includes(k) ? "bg-emerald-500/10 text-emerald-300" : k === i && !picked ? "bg-amber-400/10 text-amber-300" : "hover:bg-secondary"}`}>
                <span className="font-mono text-xs text-muted-foreground">{picked?.includes(k) ? `→${picked.indexOf(k) + 1}` : `${k + 1}.`}</span> {x.name || "—"} <span className="text-xs text-muted-foreground">· {x.city}</span>
              </button>
              {onReorder && !picked && <>
                <button type="button" disabled={k === 0} onClick={() => move(k, k - 1)} className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30" aria-label="Move up" data-testid={`route-map-up-${k}`}><ArrowUp size={13} /></button>
                <button type="button" disabled={k === stops.length - 1} onClick={() => move(k, k + 1)} className="p-1.5 text-muted-foreground hover:text-foreground disabled:opacity-30" aria-label="Move down" data-testid={`route-map-down-${k}`}><ArrowDown size={13} /></button>
              </>}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
